import React, { createContext, useState, useContext, useEffect } from 'react'
import { supabase } from '../supabaseClient'

const ProfileContext = createContext()

export const ProfileProvider = ({ children }) => {
    const [profilePicture, setProfilePicture] = useState(null)
    const [adminUsername, setAdminUsername] = useState('')
    const [adminRole, setAdminRole] = useState('')

    const updateProfilePicture = (newUrl) => {
        setProfilePicture(newUrl)
    }

    const updateAdminInfo = (username, role) => {
        setAdminUsername(username)
        setAdminRole(role)
    }

    useEffect(() => {
        const fetchInitialProfile = async () => {
            try {
                const adminEmail = sessionStorage.getItem('userEmail')
                const storedUserType = sessionStorage.getItem('userType')

                if (!adminEmail || !storedUserType) return

                if (storedUserType === 'admin') {
                    const { data: adminData, error: adminError } = await supabase
                        .from('admin_accounts')
                        .select('username, email, id')
                        .eq('email', adminEmail)
                        .single()

                    if (adminError) throw adminError

                    if (adminData) {
                        setAdminUsername(adminData.username)
                        setAdminRole("Super Admin")

                        const { data: profileData } = await supabase
                            .from('profiles')
                            .select('profile_picture_url')
                            .eq('user_id', adminData.id)
                            .eq('user_type', 'admin')
                            .maybeSingle()

                        if (profileData?.profile_picture_url) {
                            setProfilePicture(profileData.profile_picture_url)
                        }
                    }
                } else if (storedUserType === 'staff') {
                    const { data: staffData, error: staffError } = await supabase
                        .from('staff')
                        .select('email, role, id, branches ( name )')
                        .or(`email.eq.${adminEmail},company_email.eq.${adminEmail}`)
                        .single()

                    if (staffError) throw staffError

                    if (staffData) {
                        setAdminUsername(staffData.branches?.name || 'Branch Account')
                        setAdminRole(staffData.role || 'Staff')

                        const { data: profileData } = await supabase
                            .from('profiles')
                            .select('profile_picture_url')
                            .eq('user_id', staffData.id)
                            .eq('user_type', 'staff')
                            .maybeSingle()

                        if (profileData?.profile_picture_url) {
                            setProfilePicture(profileData.profile_picture_url)
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching initial profile:', err)
            }
        }

        fetchInitialProfile()
    }, [])

    useEffect(() => {
        const storedUserType = sessionStorage.getItem('userType')
        const adminEmail = sessionStorage.getItem('userEmail')

        if (!adminEmail || !storedUserType) return

        const subscription = supabase
            .channel('profile-changes')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'profiles',
                    filter: `user_type=eq.${storedUserType}`
                },
                (payload) => {
                    if (payload.new.profile_picture_url) {
                        setProfilePicture(payload.new.profile_picture_url)
                    }
                }
            )
            .subscribe()

        return () => {
            supabase.removeChannel(subscription)
        }
    }, [])

    return (
        <ProfileContext.Provider value={{ 
            profilePicture, 
            updateProfilePicture,
            adminUsername,
            adminRole,
            updateAdminInfo
        }}>
            {children}
        </ProfileContext.Provider>
    )
}

export const useProfile = () => {
    const context = useContext(ProfileContext)
    if (!context) {
        throw new Error('useProfile must be used within a ProfileProvider')
    }
    return context
}